'use client';

import { useState, useEffect, useCallback, ComponentType, useMemo } from 'react';
import { useRouter, useSearchParams, usePathname } from 'next/navigation';
import { track } from '@vercel/analytics';

// Libs
import type { PopupTranslations } from '@/lib/translations/types';

// Components
import SavingsGuidePopup from '@/components/popup/popups/SavingsGuidePopup';
import PromotionPopup from '@/components/popup/popups/PromotionPopup';

export interface PopupComponentProps {
  closePopup: () => void;
  popupId: string;
  popupData?: {
    title?: string;
    description?: string;
    imageUrl?: string;
    imageAlt?: string;
    ctaText?: string;
    ctaRoute?: string;
    disclaimer?: string;
    backgroundColor?: string;
    [key: string]: unknown;
  } | null;
  translations: PopupTranslations;
}

export type PopupComponent = ComponentType<PopupComponentProps>;

export interface PopupConfig {
  component: PopupComponent;
}

const popupRegistry: Record<string, PopupConfig> = {
  'savings-guide': {
    component: SavingsGuidePopup,
  },
  'trading-guide': {
    component: SavingsGuidePopup,
  },
  'promotion': {
    component: PromotionPopup,
  },
};

interface PopupHandlerProps {
  popupDataMap?: Record<string, PopupComponentProps['popupData']>;
  translations: PopupTranslations;
}

export default function PopupHandler({ popupDataMap = {}, translations }: PopupHandlerProps) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [isOpen, setIsOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [popupId, setPopupId] = useState<string | null>(null);
  const [content, setContent] = useState<React.ReactNode>(null);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setPopupId(null);
    setContent(null);
    
    const params = new URLSearchParams(searchParams.toString());
    params.delete('popup');
    
    const newSearch = params.toString();
    const newUrl = newSearch ? `${pathname}?${newSearch}` : pathname;
    
    router.replace(newUrl, { scroll: false });
  }, [router, pathname, searchParams]);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Memoize popup content to avoid recreating on every render
  const popupContent = useMemo(() => {
    if (!mounted) return null;
    
    const popupParam = searchParams.get('popup');
    
    if (popupParam) {
      const popupConfig = popupRegistry[popupParam];
      
      if (popupConfig) {
        const PopupComponent = popupConfig.component;
        const popupData = popupDataMap[popupParam] || null;
        return {
          popupId: popupParam,
          component: (
            <PopupComponent 
              closePopup={handleClose} 
              popupId={popupParam}
              popupData={popupData}
              translations={translations}
            />
          ),
        };
      }
    }
    
    return null;
  }, [searchParams, mounted, handleClose, popupDataMap, translations]);

  useEffect(() => {
    if (popupContent) {
      setPopupId(popupContent.popupId);
      setIsOpen(true);
      setContent(popupContent.component);
      // Vercel Analytics
      track('Open Popup', {
        type: popupContent.popupId || "",
        url: pathname || ""
      });
    } else {
      setIsOpen(false);
      setPopupId(null);
      setContent(null);
    }
  }, [pathname, popupContent]);

  if (!mounted || !isOpen || !popupId) return null;

  const popupConfig = popupRegistry[popupId];
  if (!popupConfig) return null;

  return <>{content}</>;
}

